import { formatDate } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent,
} from "@ng-bootstrap/ng-bootstrap";
import { debounce } from "lodash";
import { merge, Observable, OperatorFunction, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { EventService } from "../event-service/event.service";
import { formatNumberToHuman } from "../number-to-human/number-to-human";
import { SetFocusService } from "../set-focus/set-focus.service";
import { UpgradeModalService } from "../upgrade-modal/upgrade-modal.service";

@Component({
  selector: "ab-advanced-searchbox",
  templateUrl: "./advanced-search.component.html",
})
export class AdvancedSearchboxComponent implements OnDestroy, OnInit {
  @Input() public availableParams: any[];
  @Input() public searchQuery: string;
  @Input() public selectedParams: any[];
  @Input() public showCustomFiltersLink: boolean;
  @Input() public showUpgradeCtaAdvancedSearch: boolean;
  @Input() public showUpgradeCtaCustomFilters: boolean;
  @ViewChild("typeaheadInstance") public typeaheadInstance: NgbTypeahead;

  public clickedSearchbar: boolean;
  public focus: boolean;
  public removeParamSub: any;

  public focus$ = new Subject<string>();
  public click$ = new Subject<string>();

  public searchQueryChanged = debounce((searchQuery: string) => {
    this.eventService.publish("advanced-searchbox:updatedQuery", searchQuery);
  }, 1000);

  constructor(
    private eventService: EventService,
    private setFocusService: SetFocusService,
    private upgradeModal: UpgradeModalService,
  ) {}

  public trackByKey(_index: number, item: any) {
    return item.key;
  }

  public openUpgradeModal($event: any) {
    this.upgradeModal.open($event);
  }

  public dateSelected(searchParam: any) {
    searchParam.value = new Date(
      Date.UTC(
        searchParam.value.year,
        searchParam.value.month - 1,
        searchParam.value.day,
      ),
    );
    searchParam.label = formatDate(
      searchParam.value,
      "yyyy-MM-dd",
      "en-US",
      "UTC",
    );
    this.eventService.publish(
      "advanced-searchbox:updatedParams",
      this.selectedParams,
    );
  }

  public displaySuggestion = (suggestion: any) => {
    if (suggestion == null) {
      return;
    }

    const label = this.formatSuggestionLabel(suggestion.label);
    if (suggestion.groupCount != null) {
      const count = formatNumberToHuman(suggestion.groupCount);
      const errorText = suggestion.groupCount === 1 ? "error" : "errors";
      return `${label} (${count} ${errorText})`;
    } else {
      return label;
    }
  };

  public formatSearchQuery = (value: any) => value.name;

  public searchQueryTypeahead = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      map((term) => {
        if (term.length < 1) {
          return [];
        } else {
          return this.availableParams
            .filter((availableParam: any) => {
              return (
                availableParam.name.toLowerCase().indexOf(term.toLowerCase()) >
                -1
              );
            })
            .slice(0, 10);
        }
      }),
    );
  };

  public searchParamTypeahead: (searchParam: any) => OperatorFunction<string, any> = (
    searchParam: any
  ) => {
    return (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(
        debounceTime(250),
        distinctUntilChanged(),
      );
      const clicksWithClosedPopup$ = this.click$.pipe(
        filter(() => !this.typeaheadInstance.isPopupOpen()),
      );
      const inputFocus$ = this.focus$;

      return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
        tap(() => (searchParam.loadingResults = true)),
        switchMap((term) => {
          return searchParam.suggestions(term).pipe(
            map((suggestions: any[]) => {
              return suggestions.slice(0, 10);
            }),
          );
        }),
        tap(() => (searchParam.loadingResults = false)),
      );
    };
  }

  public resetPill(searchParam: any, $event?: any) {
    if ($event != null) {
      $event.stopPropagation();
    }
    this.resetLabelAndValue(searchParam);
    this.enterEditMode(searchParam);
  }

  public leaveEditMode(index: number) {
    if (index === undefined) {
      return;
    }

    const searchParam = this.selectedParams[index];
    if (!searchParam) {
      return;
    }
    searchParam.editMode = false;
    // remove empty search params
    if (!searchParam.value) {
      this.removeSearchParam(index);
    }
    this.eventService.publish(
      "advanced-searchbox:updatedParams",
      this.selectedParams,
    );
  }

  public searchQueryTypeaheadOnSelect($event: NgbTypeaheadSelectItemEvent) {
    $event.preventDefault();
    this.addSearchParam($event.item);
    this.resetSearchQuery();
    // Cancel any debounced calls to update the search query that are waiting to
    // execute. This prevents the search query from being cleared here and
    // then getting set back to what it was before being cleared when the
    // debounced function executes.
    this.searchQueryChanged.cancel();
  }

  public searchParamTypeaheadOnSelect(
    $event: NgbTypeaheadSelectItemEvent,
    searchParam: any,
  ) {
    $event.preventDefault();
    searchParam.label = $event.item.label;
    searchParam.value = $event.item.value;
    searchParam.editMode = false;
    this.eventService.publish(
      "advanced-searchbox:updatedParams",
      this.selectedParams,
    );
  }

  public addSearchParam(searchParam: any) {
    // Check if this search filter is available in this plan tier.
    if (searchParam.featureDisabled) {
      return;
    }

    this.addToSelected(searchParam);
    this.enterEditMode(searchParam);
    this.eventService.publish(
      "advanced-searchbox:addedSearchParam",
      searchParam,
    );
  }

  public searchboxMouseDown() {
    // prevent search suggestions from flashing quickly and disappearing
    if (this.focus) {
      this.clickedSearchbar = true;
    }
  }

  public searchboxMouseUp() {
    // use timeout to run after the mouseclick function
    setTimeout(() => (this.clickedSearchbar = false));
  }

  public searchBoxMouseClick() {
    if (this.focus) {
      return;
    }
    this.setFocusService.for("searchbox");
    this.focus = true;
  }

  public showSuggestions() {
    if (!this.availableParams) {
      return false;
    }
    return this.focus || this.clickedSearchbar;
  }

  public removeSearchParam(index: number) {
    if (index == null) {
      return;
    }

    const searchParam = this.selectedParams[index];
    this.removeFromSelected(searchParam);
    this.eventService.publish("advanced-searchbox:removedSearchParams", [
      searchParam,
    ]);
  }

  public removeAll() {
    const remainOnRemoveAllParams = this.selectedParams.filter((p: any) => {
      return p.remainOnRemoveAll;
    });
    if (this.searchQuery !== "") {
      remainOnRemoveAllParams.push({ key: "query" });
    }

    const deletedParams = [];
    if (
      remainOnRemoveAllParams.length === 0 ||
      remainOnRemoveAllParams.length === this.selectedParams.length
    ) {
      for (const param of this.selectedParams) {
        deletedParams.push(param);
      }
    } else {
      for (const param of this.selectedParams) {
        if (!param.remainOnRemoveAll) {
          deletedParams.push(param);
        }
      }
    }

    for (const param of deletedParams) {
      this.removeFromSelected(param);
    }
    this.resetSearchQuery();
    this.eventService.publish(
      "advanced-searchbox:removedSearchParams",
      deletedParams,
    );
  }

  public editPrevious(currentIndex: number) {
    if (currentIndex !== undefined) {
      this.leaveEditMode(currentIndex);
    }
    if (currentIndex > 0) {
      this.enterEditMode(this.selectedParams[currentIndex - 1]);
    } else if (this.selectedParams.length > 0) {
      this.enterEditMode(this.selectedParams[this.selectedParams.length - 1]);
    } else if (this.selectedParams.length === 0) {
      // no search parameter available anymore
      this.setFocusService.for("searchbox");
    }
  }

  public editNext(currentIndex: number) {
    if (currentIndex == null) {
      return;
    }

    this.leaveEditMode(currentIndex);
    if (currentIndex < this.selectedParams.length - 1) {
      this.enterEditMode(this.selectedParams[currentIndex + 1]);
    } else {
      this.setFocusService.for("searchbox");
    }
  }

  public keydown(e: any, searchParamIndex?: number) {
    switch (e.which) {
      case 8:
      case 37: // backspace, left-arrow
        if (this.getCurrentCaretPosition(e.target) !== 0) {
          return;
        }
        e.preventDefault();
        this.editPrevious(searchParamIndex);
        break;
      case 9: // tab
        e.preventDefault();
        if (e.shiftKey) {
          this.editPrevious(searchParamIndex);
        } else {
          this.editNext(searchParamIndex);
        }
        break;
      case 13: // enter
        this.editNext(searchParamIndex);
        break;
      case 39: // right-arrow
        if (this.getCurrentCaretPosition(e.target) !== e.target.value.length) {
          return;
        }
        this.editNext(searchParamIndex);
        break;
    }
  }

  public ngOnInit() {
    this.focus = false;
    this.removeParamSub = this.eventService.subscribe(
      "group-filter:removeParam",
      (key: string) => {
        return setTimeout(() => {
          const param = this.selectedParamByKey(key);
          if (param != null) {
            return this.removeFromSelected(param);
          }
        }, 100);
      },
    );
  }

  public ngOnDestroy() {
    this.removeParamSub.unsubscribe();
  }

  public removeEmptyDateParam(searchParam: any) {
    // Attempt to remove date param if user dismissed the datepicker without
    // selecting a date.
    if (searchParam.value) {
      return;
    }

    this.leaveEditMode(this.selectedIndexByKey(searchParam.key));
  }

  public sortParams(params: any) {
    return params.sort((a: any, b: any) => {
      if (a.featureDisabled < b.featureDisabled) {
        return -1;
      }
      if (a.featureDisabled > b.featureDisabled) {
        return 1;
      }
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
    });
  }

  private addToSelected(param: any) {
    const index = this.availableIndexByKey(param.key);
    const removed = this.availableParams.splice(index, 1)[0];
    this.selectedParams.push(removed);
  }

  private availableIndexByKey(key: any) {
    for (let index = 0; index < this.availableParams.length; index++) {
      const param = this.availableParams[index];
      if (param.key === key) {
        return index;
      }
    }
  }

  private enterEditMode(searchParam: any) {
    if (searchParam.type === "date") {
      this.setFocusService.for(`datepicker-${searchParam.key}`);
    } else {
      // timeout needed to give the pill time to render before we can enter
      // editMode and put the input in focus
      setTimeout(() => {
        searchParam.editMode = true;
        this.setFocusService.for(`searchParam:${searchParam.key}`);
      });
    }
  }

  private removeFromSelected(param: any) {
    const index = this.selectedIndexByKey(param.key);
    const removed = this.selectedParams.splice(index, 1)[0];
    this.resetLabelAndValue(removed);
    this.availableParams.push(removed);
  }

  private resetLabelAndValue(searchParam: any) {
    searchParam.label = "";
    searchParam.value = "";
  }

  private resetSearchQuery() {
    this.searchQuery = "";
    this.eventService.publish(
      "advanced-searchbox:updatedQuery",
      this.searchQuery,
    );
  }

  private selectedIndexByKey(key: string) {
    for (let index = 0; index < this.selectedParams.length; index++) {
      const param = this.selectedParams[index];
      if (param.key === key) {
        return index;
      }
    }
  }

  private selectedParamByKey(key: string) {
    for (const param of this.selectedParams) {
      if (param.key === key) {
        return param;
      }
    }
  }

  private getCurrentCaretPosition(input: HTMLInputElement) {
    if (input.selectionDirection === "backward") {
      return input.selectionStart;
    } else {
      return input.selectionEnd;
    }
  }

  private formatSuggestionLabel(label: any): string {
    if (label == null) {
      return "";
    }

    const labelAsString =
      typeof label === "object" ? JSON.stringify(label) : label.toString();

    return labelAsString.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
